import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { cn } from '@/lib/utils'
import ProjectCard from '@/components/sections/project-card'

interface ProjectGridProps {
    projects: ProjectSummary[]
    tone?: 'light' | 'dark'
    className?: string
}

/* Asymmetrical portfolio grid: a dominant first project, then alternating sizes and offsets. */
const LAYOUT = [
    { cell: 'md:col-span-8', aspect: 'aspect-[16/11]', sizes: '(min-width: 768px) 66vw, 100vw', large: true },
    { cell: 'md:col-span-4 md:mt-32', aspect: 'aspect-[3/4]', sizes: '(min-width: 768px) 33vw, 100vw', large: false },
    { cell: 'md:col-span-5 md:col-start-2', aspect: 'aspect-[4/5]', sizes: '(min-width: 768px) 42vw, 100vw', large: false },
    { cell: 'md:col-span-6 md:mt-40', aspect: 'aspect-[4/3]', sizes: '(min-width: 768px) 50vw, 100vw', large: false }
]

const PAIR = [
    { cell: 'md:col-span-7', aspect: 'aspect-[4/3]', sizes: '(min-width: 768px) 58vw, 100vw', large: true },
    { cell: 'md:col-span-5 md:mt-28', aspect: 'aspect-[4/5]', sizes: '(min-width: 768px) 42vw, 100vw', large: false }
]

const ProjectGrid: FC<ProjectGridProps> = ({ projects, tone = 'dark', className }): ReactNode => {
    const layout = projects.length === 2 ? PAIR : LAYOUT

    return (
        <div className={cn('grid gap-x-8 gap-y-16 md:grid-cols-12 md:gap-y-20', className)}>
            {projects.map((project, index) => {
                const cell = layout[index % layout.length]

                return (
                    <ProjectCard
                        key={project.id}
                        project={project}
                        index={index}
                        tone={tone}
                        aspect={cell.aspect}
                        sizes={cell.sizes}
                        large={cell.large}
                        className={cell.cell}
                    />
                )
            })}
        </div>
    )
}

export default ProjectGrid
